import { useMemo } from 'react';

import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { useAuth } from '@/contexts/auth-context';
import { useGeneratedSalesPortalRestaurantEmployeesQuery } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-restaurant-employees.generated';

interface UseRestaurantEmployeesLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseRestaurantEmployeesLoaderResult {
  employees: RestaurantEmployee[];
  error: Error | undefined;
  loading: boolean;
  refetch: () => void;
}

/**
 * Loads the list of employees for a restaurant on the given supplier.
 *
 * Excludes the currently authenticated user from the returned list — the
 * viewer should not see themselves in their own team roster.
 *
 * @param params.restaurantUUID - Restaurant whose employees are fetched
 * @param params.supplierUUID - Supplier context for the restaurant
 * @returns employees, loading, error, and refetch. `loading` stays true until
 * auth has bootstrapped to avoid flashing the empty state. `refetch` re-runs
 * the query, typically wired to the error screen's retry action.
 */
export const useRestaurantEmployeesLoader = ({
  restaurantUUID,
  supplierUUID,
}: UseRestaurantEmployeesLoaderParams): UseRestaurantEmployeesLoaderResult => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
  } = useGeneratedSalesPortalRestaurantEmployeesQuery({
    fetchPolicy: 'cache-and-network',
    skip: !authenticatedUserUUID,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  // Treat "no auth yet" as loading so consumers show the skeleton instead of
  // flashing the empty state before the bootstrap completes.
  const loading = !authenticatedUserUUID || queryLoading;

  // Employees are returned unpaginated — a single restaurant's roster is
  // expected to fit in one response, so we render the full list directly.
  const employees = useMemo(
    () =>
      (data?.getRestaurantEmployees ?? []).filter(
        (employee) => employee.employee_id !== authenticatedUserUUID,
      ),
    [data?.getRestaurantEmployees, authenticatedUserUUID],
  );

  return {
    employees,
    error,
    loading,
    refetch,
  };
};
